// React
import { useState, useRef } from 'react';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Components
import { AddOutlined, CheckBoxOutlined, CloseOutlined, GroupsOutlined, SearchOutlined } from '@mui/icons-material';
import { Avatar, Box, Button, Chip, CircularProgress, ClickAwayListener, Divider, Grow, IconButton, InputAdornment, MenuItem, MenuList, Paper, Popper, TextField, Tooltip, Typography } from '@mui/material';

// Style
import { blue, red, orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Utils
import { getConsistentAvatarColor } from '../../utils/index.js';

/**
 * A reusable searchable athlete selection component with popup menu
 * Used in AddUser and EditUser dialogs for selecting multiple athletes
 */
const SearchableAthleteSelector = (props) => {
    const { athletes, selectedAthletes, onChangeSelectedAthletes, label = "Athleten", color, loading = false } = props;
    const { isDarkMode } = useAppTheme();
    
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddMenu, setOpenAddMenu] = useState(false);
    const anchorRef = useRef(null);
    

    const accentColor = color || (isDarkMode ? orange[500] : blue[500]);
    
    const convertBufferToBase64String = (athlete) => {
        if (!athlete?.profile?.picture?.data) return '';
        return btoa(String.fromCharCode(...new Uint8Array(athlete.profile.picture.data)));
    }
    
    const handleToggleAddMenu = () => {
        setOpenAddMenu((prevOpen) => !prevOpen);
    };
    
    const handleCloseAddMenu = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        event?.stopPropagation();
        setOpenAddMenu(false);
        setSearchTerm('');
    };
    
    const handleAddAthlete = (athlete) => {
        const newSelected = [...selectedAthletes, athlete];
        onChangeSelectedAthletes({ target: { value: newSelected } });
        setSearchTerm('');
    };
    
    const handleRemoveAthlete = (athleteToRemove) => {
        const newSelected = selectedAthletes.filter(
            athlete => athlete.profile.email !== athleteToRemove.profile.email
        );
        onChangeSelectedAthletes({ target: { value: newSelected } });
    };
    
    const handleSelectAll = () => {
        const newSelected = [...selectedAthletes, ...availableAthletes];
        onChangeSelectedAthletes({ target: { value: newSelected } });
        setOpenAddMenu(false);
        setSearchTerm('');
    };
    
    // Filter athletes for dropdown: exclude already selected ones
    const availableAthletes = athletes
        .filter(athlete => 
            athlete.profile.lastName !== null && 
            !selectedAthletes.some(selected => selected.profile.email === athlete.profile.email) &&
            (
                athlete.profile.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                athlete.profile.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                athlete.squad?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        )
        .sort((a, b) => String(a.profile.lastName).localeCompare(String(b.profile.lastName)));

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Label */}
            <Typography 
                variant="caption" 
                sx={{ 
                    display: 'block',
                    mb: 1,
                    ml: 0.5,
                    color: isDarkMode ? darkTheme.textSecondary : 'text.secondary',
                    fontWeight: 500
                }}
            >
                {label}
            </Typography>

            {/* Selected Athletes Display */}
            <Box 
                sx={{ 
                    minHeight: 56,
                    border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: isDarkMode ? darkTheme.headerBg : 'white',
                    position: 'relative',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                        borderColor: accentColor,
                    }
                }}
            >
                {selectedAthletes.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <GroupsOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 20 }} />
                        <Typography variant="body2" color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>
                            {selectedAthletes.length} {selectedAthletes.length === 1 ? 'Athlet' : 'Athleten'} ausgewählt
                        </Typography>
                    </Box>
                )}

                {/* Add Button */}
                <Tooltip title="Athlet hinzufügen">
                    <IconButton 
                        ref={anchorRef}
                        size="small"
                        onClick={handleToggleAddMenu}
                        sx={{ 
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: accentColor,
                            color: 'white',
                            width: 32,
                            height: 32,
                            '&:hover': {
                                bgcolor: color ? `${color}cc` : (isDarkMode ? orange[700] : blue[700]),
                            }
                        }}
                    >
                        <AddOutlined sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>

                {/* Selected Athletes Chips */}
                {selectedAthletes.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {selectedAthletes
                            .filter(athlete => athlete.profile.lastName !== null)
                            .sort((a, b) => String(a.profile.lastName).localeCompare(String(b.profile.lastName)))
                            .map((athlete) => (
                                <Chip 
                                    key={athlete.profile.email}
                                    avatar={
                                        <Avatar 
                                            src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                            sx={{ 
                                                bgcolor: getConsistentAvatarColor(athlete),
                                                width: 24,
                                                height: 24,
                                                fontSize: '0.7rem',
                                                '& .MuiAvatar-fallback': {
                                                    fill: 'white'
                                                }
                                            }}
                                        />
                                    }
                                    label={`${athlete.profile.lastName}, ${athlete.profile.firstName}`}
                                    onDelete={() => handleRemoveAthlete(athlete)}
                                    size="small"
                                    sx={{ 
                                        bgcolor: isDarkMode ? darkTheme.cardBg : 'rgba(0, 0, 0, 0.04)',
                                        border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)',
                                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                                        '& .MuiChip-avatar': {
                                            color: 'white'
                                        },
                                        '& .MuiChip-deleteIcon': {
                                            color: isDarkMode ? darkTheme.textSecondary : 'rgba(0, 0, 0, 0.4)',
                                            '&:hover': {
                                                color: red[500]
                                            }
                                        }
                                    }}
                                />
                            ))}
                    </Box>
                )}

                {selectedAthletes.length === 0 && (
                    <Box 
                        sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            py: 2,
                            px: 2,
                            bgcolor: isDarkMode ? darkTheme.headerBg : 'white',
                            borderRadius: 1.5,
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ color: color || (isDarkMode ? darkTheme.textSecondary : 'action.disabled') }} />
                                <Typography 
                                    variant="body2" 
                                    color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}
                                >
                                    Athleten werden geladen...
                                </Typography>
                            </>
                        ) : (
                            <>
                                <GroupsOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.disabled', fontSize: 24 }} />
                                <Typography 
                                    variant="body2" 
                                    color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}
                                    sx={{ fontStyle: 'italic' }}
                                >
                                    Keine Athleten ausgewählt
                                </Typography>
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {/* Searchable Popup Menu */}
            <Popper
                open={openAddMenu}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="auto"
                transition
                disablePortal={false}
                modifiers={[
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            altBoundary: true,
                            rootBoundary: 'viewport',
                            padding: 8,
                        },
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            altAxis: true,
                            altBoundary: true,
                            tether: true,
                            rootBoundary: 'viewport',
                            padding: 8,
                        },
                    },
                ]}
                sx={{ zIndex: 1400 }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' || placement === 'bottom-end' || placement === 'bottom-start'
                                ? 'center top'
                                : 'center bottom',
                        }}
                    >
                        <Paper
                            elevation={8}
                            sx={{
                                mt: placement === 'bottom' || placement === 'bottom-end' || placement === 'bottom-start' ? 1 : 0,
                                mb: placement === 'top' || placement === 'top-end' || placement === 'top-start' ? 1 : 0,
                                minWidth: 320,
                                maxWidth: 420,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: isDarkMode ? '0 12px 48px rgba(0, 0, 0, 0.6)' : '0 12px 48px rgba(0, 0, 0, 0.3)',
                                border: isDarkMode ? `2px solid ${darkTheme.cardBorder}` : '2px solid rgba(0, 0, 0, 0.1)',
                                bgcolor: isDarkMode ? darkTheme.bg : undefined,
                            }}
                        >
                            <ClickAwayListener onClickAway={handleCloseAddMenu}>
                                <Box>
                                    {/* Search Header */}
                                    <Box sx={{ p: 2, bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(0, 0, 0, 0.02)', position: 'relative' }}>
                                        {/* Close Button */}
                                        <IconButton
                                            size="small"
                                            onClick={handleCloseAddMenu}
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                color: isDarkMode ? darkTheme.textSecondary : 'text.secondary',
                                                '&:hover': {
                                                    bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.08)',
                                                    color: isDarkMode ? darkTheme.textPrimary : 'text.primary'
                                                }
                                            }}
                                        >
                                            <CloseOutlined sx={{ fontSize: 18 }} />
                                        </IconButton>
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, pr: 4 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Athlet suchen..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                autoFocus
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchOutlined sx={{ fontSize: 20, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: searchTerm && (
                                                        <InputAdornment position="end">
                                                            <IconButton 
                                                                size="small"
                                                                onClick={() => setSearchTerm('')}
                                                                sx={{ 
                                                                    p: 0.5,
                                                                    color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                                }}
                                                            >
                                                                <CloseOutlined sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 1.5,
                                                        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                        '& input': {
                                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                            '&::placeholder': {
                                                                color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                                opacity: 1,
                                                            }
                                                        },
                                                        '& fieldset': {
                                                            borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: accentColor,
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: accentColor,
                                                        }
                                                    }
                                                }}
                                            />
                                        </Box>
                                        {availableAthletes.length > 0 && (
                                            <Button
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                startIcon={<CheckBoxOutlined />}
                                                onClick={handleSelectAll}
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: 1.5,
                                                    borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)',
                                                    color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
                                                    '&:hover': {
                                                        borderColor: accentColor,
                                                        bgcolor: isDarkMode ? (color ? `${color}22` : `${accentColor}22`) : (color ? `${color}11` : blue[50]),
                                                    }
                                                }}
                                            >
                                                Alle auswählen ({availableAthletes.length})
                                            </Button>
                                        )}
                                    </Box>
                                    <Divider sx={{ borderColor: isDarkMode ? darkTheme.cardBorder : undefined }} />
                                    
                                    {/* Athletes List */}
                                    <MenuList
                                        sx={{
                                            maxHeight: 320,
                                            overflow: 'auto',
                                            p: 1,
                                            bgcolor: isDarkMode ? darkTheme.cardBg : undefined,
                                        }}
                                    >
                                        {availableAthletes.length === 0 ? (
                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                <Typography variant="body2" color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>
                                                    {searchTerm ? 'Keine Athleten gefunden' : 'Alle Athleten ausgewählt'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            availableAthletes.map((athlete) => (
                                                <MenuItem
                                                    key={athlete.profile.email}
                                                    onClick={() => handleAddAthlete(athlete)}
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        mb: 0.5,
                                                        py: 1,
                                                        '&:hover': {
                                                            bgcolor: isDarkMode ? (color ? `${color}22` : `${accentColor}22`) : (color ? `${color}11` : blue[50]),
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                        <Avatar
                                                            src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                bgcolor: getConsistentAvatarColor(athlete),
                                                                fontSize: '0.875rem',
                                                            }}
                                                        />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={500} noWrap sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                                                                {athlete.profile.lastName}, {athlete.profile.firstName}
                                                            </Typography>
                                                            <Typography variant="caption" color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} noWrap>
                                                                {athlete.squad}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </MenuItem>
                                            ))
                                        )}
                                    </MenuList>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    );
};

export default SearchableAthleteSelector;
